/**
 * Profile360 Cache Service
 * Enterprise-grade caching system for improved performance
 */

import profile360Config from '../config/profile360Config';

/**
 * Cache Service class for Profile360
 */
class CacheService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.accessCount = new Map();
    this.maxSize = profile360Config.get('performance.caching.maxSize', 100);
    this.ttl = profile360Config.get('performance.caching.ttl', 300000); // 5 minutes
    this.enabled = profile360Config.get('performance.caching.enabled', true);
    
    // Cleanup interval
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 60000); // Cleanup every minute
  }

  /**
   * Generate cache key from query and parameters
   * @param {string} query - Query string
   * @param {Object} params - Query parameters
   * @returns {string} Cache key
   */
  generateKey(query, params = {}) {
    const normalizedQuery = query.toLowerCase().trim();
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${normalizedQuery}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Get cached result
   * @param {string} query - Query string
   * @param {Object} params - Query parameters
   * @returns {Object|null} Cached result or null
   */
  get(query, params = {}) {
    if (!this.enabled) return null;

    const key = this.generateKey(query, params);
    const timestamp = this.timestamps.get(key);
    
    // Check if cache entry exists and is not expired
    if (timestamp && (Date.now() - timestamp) < this.ttl) {
      // Update access count for LRU
      this.accessCount.set(key, (this.accessCount.get(key) || 0) + 1);
      
      const result = this.cache.get(key);
      return result;
    }

    // Remove expired entry
    if (timestamp) {
      this.remove(key);
    }

    return null;
  }

  /**
   * Set cache entry
   * @param {string} query - Query string
   * @param {Object} params - Query parameters
   * @param {Object} result - Result to cache
   */
  set(query, params = {}, result) {
    if (!this.enabled) return;

    const key = this.generateKey(query, params);
    
    // Check cache size and evict if necessary
    if (this.cache.size >= this.maxSize) {
      this.evictLeastUsed();
    }

    this.cache.set(key, result);
    this.timestamps.set(key, Date.now());
    this.accessCount.set(key, 1);
  }

  /**
   * Remove cache entry
   * @param {string} key - Cache key
   */
  remove(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
    this.accessCount.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.accessCount.clear();
  }

  /**
   * Evict least recently used entries
   */
  evictLeastUsed() {
    // Find the least accessed entry
    let leastUsedKey = null;
    let leastAccessCount = Infinity;

    for (const [key, count] of this.accessCount.entries()) {
      if (count < leastAccessCount) {
        leastAccessCount = count;
        leastUsedKey = key;
      }
    }

    if (leastUsedKey) {
      this.remove(leastUsedKey);
    }
  }

  /**
   * Cleanup expired entries
   */
  cleanup() {
    const now = Date.now();
    const expiredKeys = [];

    for (const [key, timestamp] of this.timestamps.entries()) {
      if ((now - timestamp) >= this.ttl) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => this.remove(key));
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      enabled: this.enabled,
      ttl: this.ttl,
      entries: Array.from(this.cache.keys()).map(key => ({
        key: key.substring(0, 50) + '...',
        timestamp: this.timestamps.get(key),
        accessCount: this.accessCount.get(key),
        age: Date.now() - this.timestamps.get(key)
      }))
    };
  }

  /**
   * Enable caching
   */
  enable() {
    this.enabled = true;
  }

  /**
   * Disable caching
   */
  disable() {
    this.enabled = false;
    this.clear();
  }

  /**
   * Update cache configuration
   * @param {Object} config - New configuration
   */
  updateConfig(config) {
    if (config.maxSize !== undefined) {
      this.maxSize = config.maxSize;
    }
    if (config.ttl !== undefined) {
      this.ttl = config.ttl;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }

  /**
   * Preload cache with common queries
   * @param {Array} queries - Array of {query, params, result} objects
   */
  preload(queries) {
    queries.forEach(({ query, params, result }) => {
      this.set(query, params, result);
    });
  }

  /**
   * Destroy cache service
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clear();
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;
